import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Color } from 'three';
import { NFT_IMAGE_SIZE_PX } from '../constants';

import { FloorType } from '../types/floor';

import {
  getCoordinatesFromId,
  getWorldPositionFromNFT,
  isCoordinateAnNFT,
  loadTexturePromise,
  randomFallbackImage as getFallbackImage,
} from '../utils/floor_utils';
import TextureMerger from '../utils/TextureMerger';

import { useFloorContext } from '../context/FloorContext';
import { useNFTContext } from '../context/NFTContext';
import { useTargetContext } from '../context/TargetContext';
import { useSearchContext } from '../context/SearchContext';
import { useRoomContext, useRoomsByOwner } from '../context/RoomContext';
import { useUserContext } from '../context/UserContext';

const Atlas = ({
  width,
  height,
  boxSize,
  floorMap,
}: {
  width: number;
  height: number;
  boxSize: number;
  floorMap: FloorType;
}) => {
  const { target } = useTargetContext();
  const {
    filteredNFTs,
    selectedNFT,
    setSelectedNFT,
    isNFTPlacingMode,
    tempSelectedNFTImage,
  } = useNFTContext();
  const { setIsFetching, currentFloor } = useFloorContext();
  const { isFiltered } = useSearchContext();
  const { cachedRooms } = useRoomContext();

  const { wallet_address, connected } = useUserContext();
  const { rooms } = useRoomsByOwner(connected ? wallet_address : '');

  const refInstancedMesh = useRef<THREE.InstancedMesh>(null);
  const instancedGeometry = useMemo<THREE.BufferGeometry>(
    () => new THREE.BoxGeometry(boxSize * 0.99, boxSize, boxSize * 0.99),
    [boxSize]
  );

  const [atlasSizeX, setAtlasSizeX] = useState(0);
  const [atlasSizeY, setAtlasSizeY] = useState(0);

  const [atlasMerger, setAtlasMerger] = useState<TextureMerger>();
  const [textureAtlas, setTextureAtlas] = useState<THREE.CanvasTexture>();
  const [idIndexes, setIdIndexes] = useState<string[]>([]);

  const changeSelectedNFT = (id: number | undefined) => {
    if (id !== undefined && !isNFTPlacingMode) {
      const coordinates = getCoordinatesFromId(id, width, height);
      const nftObject = isCoordinateAnNFT(
        coordinates.x,
        coordinates.y,
        floorMap
      );

      if (nftObject) {
        if (nftObject.roomId !== selectedNFT?.roomId) {
          setSelectedNFT(nftObject);
          target === 'dev' && console.log('NFT selected: ' + nftObject.roomId);
        }
      } else {
        // reset selection, how?
        // setSelectedNFT(undefined);
      }
    }
  };

  const material = useMemo(() => {
    if (textureAtlas && atlasMerger && atlasMerger.textureOffsets) {
      let atlasX = 1;
      let atlasY = 1;

      const totalUniqueTextures = Object.values(
        atlasMerger.textureOffsets
      ).filter(
        (v: any, i, a) =>
          a.findIndex((v2: any) => v2.x === v.x && v2.y === v.y) === i
      ).length;

      for (let i = 0; i < totalUniqueTextures; i++) {
        const numOfImageNow = i + 1;
        if (numOfImageNow > atlasX * atlasY) {
          if (atlasX === atlasY) {
            atlasX++;
          } else {
            atlasY++;
          }
        }
      }

      const ma = new THREE.MeshBasicMaterial({
        map: atlasMerger.mergedTexture,
        transparent: true,
      });
      ma.onBeforeCompile = (shader) => {
        shader.vertexShader = shader.vertexShader.replace(
          `void main() {`,
          `
          attribute float tileNumX; // number between 0 and 1
          attribute float tileNumY; // number between 0 and 1
          attribute float aspectRatio; // aspect ratio
          uniform vec2 atlasXY; // number of tiles in the atlas X and Y

          void main() {
          
          `
        );

        shader.vertexShader = shader.vertexShader.replace(
          `#include <uv_vertex>`,
          `
          #include <uv_vertex>
          
          vec2 tileXY = vec2(tileNumX,tileNumY);
          vUv /= atlasXY;
          
          // // Adjust for aspect ratio
          if (aspectRatio > 1.0) {
            // Wider than tall, adjust x coordinate
            vUv.y /= aspectRatio;
            vUv.y += (1.0 - 1.0 / aspectRatio) / (atlasXY.y * 2.0); // Center vertically
        } else if (aspectRatio < 1.0) {
            // Taller than wide, adjust y coordinate
            vUv.x *= aspectRatio;
            vUv.x += (1.0 - aspectRatio) / (atlasXY.x * 2.0); // Center horizontally
          }

          vUv += tileXY/atlasXY;
          `
        );

        target === 'dev' && console.log('atlas XY: ' + atlasX + ' ' + atlasY);

        setAtlasSizeX(atlasX);
        setAtlasSizeY(atlasY);

        shader.uniforms.atlasXY = {
          value: new THREE.Vector2(atlasX, atlasY),
        };
      };
      // ma.needsUpdate = true;
      return ma;
    }
    // filteredNFTs change should re-compile the material
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atlasMerger, textureAtlas, filteredNFTs]);

  useEffect(() => {
    if (selectedNFT && !tempSelectedNFTImage) {
      return;
    }

    setIsFetching(true);
    // setTextureAtlas(undefined); handled in next useEffect

    const imagePromises: Promise<any>[] = [];
    const tempIdIndexes: string[] = [];

    floorMap.rooms.forEach(function (el) {
      tempIdIndexes.push(el.roomId);

      if (el.roomId === selectedNFT?.roomId && tempSelectedNFTImage) {
        imagePromises.push(loadTexturePromise(tempSelectedNFTImage));
        return;
      }

      const actualMapIconSize = `${el?.endX - el?.startX + 1}x${
        el?.endY - el?.startY + 1
      }`;

      if (actualMapIconSize !== '1x1') {
        if (el.mapIconUrl) {
          const isMyRoom =
            rooms.findIndex((room) => room.roomId === el.roomId) >= 0 &&
            el.mapIconStatus === 'AWAITING_APPROVAL';

          const textureUrl =
            el.mapIconStatus === 'APPROVED' || isMyRoom
              ? el.mapIconUrl
              : getFallbackImage(el.roomId);

          imagePromises.push(loadTexturePromise(textureUrl));
          return;
        }

        if (actualMapIconSize !== '2x1') {
          imagePromises.push(loadTexturePromise(getFallbackImage(el.roomId)));
          return;
        }
      }

      tempIdIndexes.pop();
    });

    if (imagePromises.length) {
      Promise.all(imagePromises)
        .then((values) => {
          const texturesObj: any = {};
          values.forEach((value, index) => {
            texturesObj[index.toString()] = value;
          });

          const textureMerger = new TextureMerger(
            texturesObj,
            NFT_IMAGE_SIZE_PX
          );

          target === 'dev' && console.log('Loaded textures:');
          target === 'dev' && console.log(texturesObj);

          target === 'dev' && console.log('Atlas created, offsets:');
          target === 'dev' && console.log(textureMerger.textureOffsets);

          setTextureAtlas(textureMerger.mergedTexture);
          setAtlasMerger(textureMerger);
          setIdIndexes(tempIdIndexes);
          setIsFetching(false);
        })
        .catch((e) => console.error(e));
    } else {
      setIsFetching(false);
    }
  }, [floorMap, selectedNFT, tempSelectedNFTImage, currentFloor, cachedRooms]); // eslint-disable-line

  useEffect(() => {
    setTextureAtlas(undefined);
  }, [currentFloor, floorMap]); //eslint-disable-line

  const idBuffer = useCallback(
    (atlastTextureId: number) => {
      const idb = new Float32Array(width * height);
      for (let i = 0; i < width * height; i++) idb[i] = atlastTextureId;
      return idb;
    },
    [width, height]
  );

  const idAttr = useMemo(
    () => new THREE.InstancedBufferAttribute(idBuffer(0), 1),
    [idBuffer]
  );
  const tileNumX = useMemo(
    () => new THREE.InstancedBufferAttribute(idBuffer(0), 1),
    [idBuffer]
  );
  const tileNumY = useMemo(
    () => new THREE.InstancedBufferAttribute(idBuffer(0), 1),
    [idBuffer]
  );
  const aspectRatio = useMemo(
    () => new THREE.InstancedBufferAttribute(idBuffer(0), 1),
    [idBuffer]
  );
  instancedGeometry.setAttribute('index', idAttr);
  instancedGeometry.setAttribute('tileNumX', tileNumX);
  instancedGeometry.setAttribute('tileNumY', tileNumY);
  instancedGeometry.setAttribute('aspectRatio', aspectRatio);

  useEffect(() => {
    if (
      textureAtlas &&
      refInstancedMesh.current &&
      atlasMerger &&
      atlasMerger.textureOffsets &&
      material &&
      atlasSizeX > 0 &&
      atlasSizeY > 0
    ) {
      // check if floor was changed
      if (Object.keys(atlasMerger.textureOffsets).length !== idIndexes.length) {
        target === 'dev' && console.log('Atlas is being updated, waiting...');
      } else {
        target === 'dev' && console.log('Atlas has been updated');

        const dummy = new THREE.Object3D();
        let counter = 0;

        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const id = counter++;

            const coordinates = getCoordinatesFromId(id, width, height);
            const nft = isCoordinateAnNFT(
              coordinates.x,
              coordinates.y,
              floorMap
            );

            refInstancedMesh.current.setColorAt(id, new Color());

            if (nft) {
              const nftWidth = nft?.endX - nft?.startX + 1;
              const nftHeight = nft?.endY - nft?.startY + 1;

              const actualMapIconSize = `${nftWidth}x${nftHeight}`;

              if (
                idIndexes.includes(nft.roomId) &&
                // TODO: for now disable rendering of map icons for small rooms due to load
                // should be enabled after atlas is coming from the backend in future
                // actualMapIconSize !== '2x1' &&
                actualMapIconSize !== '1x1'
              ) {
                const nftImageIndex = idIndexes.findIndex(
                  (id) => id === nft.roomId
                );

                const nftTextureOffset = Object.values(
                  atlasMerger.textureOffsets
                )[nftImageIndex] as any;

                const nftAtlasTileNumX = Math.ceil(
                  (nftTextureOffset.x / (atlasSizeX * NFT_IMAGE_SIZE_PX)) *
                    atlasSizeX
                );

                const nftAtlasTileNumY =
                  atlasSizeY -
                  1 -
                  Math.ceil(
                    (nftTextureOffset.y / (atlasSizeY * NFT_IMAGE_SIZE_PX)) *
                      atlasSizeY
                  );

                const a = idAttr.array as Float32Array;
                a[id] = nftImageIndex;
                idAttr.needsUpdate = true;

                const ox = tileNumX.array as Float32Array;
                ox[id] = nftAtlasTileNumX;
                tileNumX.needsUpdate = true;

                const oy = tileNumY.array as Float32Array;
                oy[id] = nftAtlasTileNumY;
                tileNumY.needsUpdate = true;

                const ar = aspectRatio.array as Float32Array;
                ar[id] = nftWidth / nftHeight;
                aspectRatio.needsUpdate = true;

                let position = {
                  x: Number.MAX_SAFE_INTEGER,
                  y: Number.MAX_SAFE_INTEGER,
                  z: Number.MAX_SAFE_INTEGER,
                };

                let scale = new THREE.Vector3(1, 1, 1);
                dummy.scale.set(1, 1, 1);

                // draw only one big box for the whole nft
                if (
                  nft.isPlaced &&
                  nft.endX > -1 &&
                  nft.startX > -1 &&
                  nft.endY > -1 &&
                  nft.startY > -1 &&
                  nft.startX === x &&
                  nft.startY === y
                ) {
                  const amountOfBoxesX = nft.endX - nft.startX + 1;
                  const amountOfBoxesZ = nft.endY - nft.startY + 1;

                  scale = new THREE.Vector3(
                    amountOfBoxesX * 0.999,
                    1,
                    amountOfBoxesZ * 0.999
                  );

                  position = getWorldPositionFromNFT(
                    nft,
                    boxSize,
                    amountOfBoxesX,
                    amountOfBoxesZ
                  ) ?? {
                    x: Number.MAX_SAFE_INTEGER,
                    y: Number.MAX_SAFE_INTEGER,
                    z: Number.MAX_SAFE_INTEGER,
                  };
                }

                if (isFiltered || filteredNFTs.length) {
                  if (
                    filteredNFTs.findIndex((n) => n.roomId === nft.roomId) ===
                    -1
                  ) {
                    refInstancedMesh.current.setColorAt(id, new Color('#555'));
                    target === 'dev' && console.log('color set?');
                  }
                }

                dummy.scale.multiply(scale);
                dummy.rotation.set(0, Math.PI * 1.5, 0);
                dummy.position.set(position.x, position.y, position.z);
              } else {
                dummy.position.set(
                  Number.MAX_SAFE_INTEGER,
                  Number.MAX_SAFE_INTEGER,
                  Number.MAX_SAFE_INTEGER
                );
              }

              dummy.updateMatrix();
              refInstancedMesh.current.setMatrixAt(id, dummy.matrix);

              if (refInstancedMesh.current.instanceColor)
                refInstancedMesh.current.instanceColor.needsUpdate = true;
            }
          }
        }

        refInstancedMesh.current.instanceMatrix.needsUpdate = true;
      }
    }
  }, [
    atlasMerger,
    atlasSizeX,
    atlasSizeY,
    boxSize,
    floorMap,
    height,
    idAttr,
    material,
    textureAtlas,
    width,
    filteredNFTs,
    tileNumX,
    tileNumY,
    target,
    idIndexes,
    isFiltered,
    aspectRatio,
  ]);

  if (!material || !instancedGeometry) {
    return null;
  }

  return (
    <instancedMesh
      ref={refInstancedMesh}
      args={[undefined, undefined, width * height]}
      position={[0, 0, 0]}
      renderOrder={120}
      geometry={instancedGeometry}
      material={material}
      onClick={(e) => {
        e.stopPropagation();
        changeSelectedNFT(e.instanceId);
      }}
    ></instancedMesh>
  );
};

export default Atlas;
